import React from 'react';
import { Award, Target, Heart, Users, BookOpen, Globe } from 'lucide-react';
import { aboutInfo, centerInfo, stats } from '../utils/mockData';
import { Card, CardContent } from '../components/ui/card';

export default function About() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0039A6] to-[#007A5E] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">À Propos de Nous</h1>
            <p className="text-xl text-white/90">
              Une institution dédiée à l'excellence dans l'enseignement de la langue russe
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <Card className="border-t-4 border-t-[#0039A6]">
              <CardContent className="pt-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#0039A6] rounded-lg flex items-center justify-center">
                    <Target className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Notre Mission</h2>
                </div>
                <p className="text-gray-700 leading-relaxed">{aboutInfo.mission}</p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#D52B1E]">
              <CardContent className="pt-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#D52B1E] rounded-lg flex items-center justify-center">
                    <Award className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Notre Vision</h2>
                </div>
                <p className="text-gray-700 leading-relaxed">{aboutInfo.vision}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Notre Histoire
              </h2>
              <div className="w-20 h-1 bg-[#FCD116] mx-auto"></div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {aboutInfo.history}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-3xl font-bold text-[#0039A6] mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos Valeurs
              </h2>
              <p className="text-lg text-gray-600">
                Les principes qui guident notre enseignement et nos relations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {aboutInfo.values.map((value, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-[#007A5E] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{value}</h3>
                    <p className="text-gray-600">
                      Nous nous engageons à maintenir les plus hauts standards dans tous les aspects de notre travail.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-[#0039A6] to-[#007A5E] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Pourquoi Choisir Notre Centre ?
              </h2>
              <p className="text-xl text-white/80">
                Une combinaison unique d'expertise et d'innovation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users size={32} />
                </div>
                <h3 className="text-xl font-bold mb-3">Professeurs Expérimentés</h3>
                <p className="text-white/80">
                  Équipe de professeurs natifs et certifiés avec plus de 10 ans d'expérience
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen size={32} />
                </div>
                <h3 className="text-xl font-bold mb-3">Méthodes Modernes</h3>
                <p className="text-white/80">
                  Approche communicative innovante pour un apprentissage rapide et efficace
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe size={32} />
                </div>
                <h3 className="text-xl font-bold mb-3">Reconnaissance Internationale</h3>
                <p className="text-white/80">
                  Certificats TORFL reconnus par toutes les universités russes et internationales
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#FCD116]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Vous avez des questions ?
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            Notre équipe est à votre disposition pour vous guider
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${centerInfo.phone}`}
              className="bg-[#0039A6] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#0052d4] transition-colors inline-block"
            >
              Appelez-nous
            </a>
            <a
              href={`mailto:${centerInfo.email}`}
              className="bg-white text-[#0039A6] px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-block"
            >
              Envoyez un email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
